import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ExcessPaymentsService, ExcessPaymentResponseDTO, PaymentGroupSummaryDTO } from '../../services/excess-payments.service';
import { ToastService } from '../../services/toast.service';
import { AuditInfo } from '../../interfaces/audit.interfaces';

@Component({
  selector: 'app-excess-payments',
  templateUrl: './excess-payments.component.html',
  styleUrls: ['./excess-payments.component.scss']
})
export class ExcessPaymentsComponent implements OnInit {
  excessPayments: ExcessPaymentResponseDTO[] = [];
  filteredExcessPayments: ExcessPaymentResponseDTO[] = [];
  loading = false;
  searchTerm = '';
  statusFilter = '';
  excessTypeFilter = ''; // 🆕 FILTRO POR TIPO DE EXCEDENTE
  
  // Paginación
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 0;

  // Exponer Math para uso en el template
  Math = Math;

  // Modal del resumen financiero
  showFinancialSummaryModal = false;
  selectedPaymentGroupSummary: PaymentGroupSummaryDTO | null = null;
  loadingFinancialSummary = false;

  // Filtros disponibles
  statusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'Available', label: 'Disponible' },
    { value: 'Processing', label: 'En procesamiento' },
    { value: 'Refunded', label: 'Devuelto' },
    { value: 'Expired', label: 'Expirado' }
  ];

  // 🆕 FILTRO POR TIPO DE EXCEDENTE
  excessTypeOptions = [
    { value: '', label: 'Todos los tipos' },
    { value: 'individual', label: 'Individual' },
    { value: 'corporativo', label: 'Corporativo' }
  ];

  constructor(
    private excessPaymentsService: ExcessPaymentsService,
    private router: Router,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadExcessPayments();
  }

  loadExcessPayments(): void {
    this.loading = true;
    this.excessPaymentsService.getAll().subscribe({
      next: (result: any) => {
        if (result.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const data = result?.data;
          this.excessPayments = Array.isArray(data) ? data : (data?.data || []);
          this.applyFilters();
          this.toastService.showToast('success', 'Excedentes cargados exitosamente');
        } else {
          this.toastService.showToast('error', result.errorMessage || 'Error al cargar excedentes');
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading excess payments:', error);
        this.toastService.showToast('error', 'Error al cargar excedentes');
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.excessPayments];

    // Filtro por término de búsqueda
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(ep => {
        // 🆕 MANEJAR CASOS NULL: Verificar si participant existe
        const participantMatch = ep.participant ? (
          ep.participant.firstName.toLowerCase().includes(term) ||
          ep.participant.lastName.toLowerCase().includes(term) ||
          ep.participant.documentId.toLowerCase().includes(term)
        ) : false;

        // 🆕 BÚSQUEDA EN COMPANY para excedentes corporativos
        const companyMatch = ep.company ? (
          ep.company.name.toLowerCase().includes(term) ||
          ep.company.documentId.toLowerCase().includes(term)
        ) : false;

        return participantMatch ||
               companyMatch ||
               ep.project.name.toLowerCase().includes(term) ||
               ep.concept.toLowerCase().includes(term) ||
               ep.originIncomeReceipt.referenceNumber.toLowerCase().includes(term);
      });
    }

    // Filtro por estado
    if (this.statusFilter) {
      filtered = filtered.filter(ep => ep.status === this.statusFilter);
    }

    // 🆕 FILTRO POR TIPO DE EXCEDENTE
    if (this.excessTypeFilter) {
      filtered = filtered.filter(ep => {
        if (this.excessTypeFilter === 'individual') {
          return ep.isIndividualPayment;
        } else if (this.excessTypeFilter === 'corporativo') {
          return ep.isCorporatePayment;
        }
        return true;
      });
    }

    this.filteredExcessPayments = filtered;
    this.totalItems = filtered.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.currentPage = 1; // Reset to first page when filtering
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  // 🆕 MANEJO DEL FILTRO DE TIPO DE EXCEDENTE
  onExcessTypeFilterChange(): void {
    this.applyFilters();
  }

  getPaginatedData(): ExcessPaymentResponseDTO[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredExcessPayments.slice(startIndex, endIndex);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatCurrency(amount: number, currencyCode?: string): string {
    const code = currencyCode || 'COP';
    const minimumFractionDigits = code === 'COP' ? 0 : 2;
    const maximumFractionDigits = code === 'COP' ? 0 : 2;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount ?? 0);
  }

  // 🆕 OBTENER NOMBRE DEL PAGADOR (Participante o Empresa)
  getPayerName(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.participant) {
      return `${excessPayment.participant.firstName} ${excessPayment.participant.lastName}`;
    } else if (excessPayment.company) {
      return excessPayment.company.name;
    }
    return 'Sin información';
  }

  // 🆕 OBTENER IDENTIFICACIÓN DEL PAGADOR
  getPayerDocumentId(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.participant) {
      return excessPayment.participant.documentId;
    } else if (excessPayment.company) {
      return excessPayment.company.documentId;
    }
    return 'N/A';
  }

  // 🆕 OBTENER TIPO DEL PAGADOR PARA MOSTRAR
  getPayerType(excessPayment: ExcessPaymentResponseDTO): string {
    return excessPayment.isCorporatePayment ? '🏢 Corporativo' : '👤 Individual';
  }

  // 🆕 OBTENER CÓDIGO DE MONEDA CORRECTO
  getCurrencyCode(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.enrollment?.currencyCode) {
      return excessPayment.enrollment.currencyCode;
    } else if (excessPayment.corporateEnrollment?.currencyCode) {
      return excessPayment.corporateEnrollment.currencyCode;
    }
    return 'COP'; // Default
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  viewDetail(excessPayment: ExcessPaymentResponseDTO): void {
    this.router.navigate(['/dashboard/excess-payments', excessPayment.id]);
  }

  refreshData(): void {
    this.loadExcessPayments();
  }

  /**
   * Mostrar resumen financiero del grupo de pagos
   */
  viewFinancialSummary(excessPayment: ExcessPaymentResponseDTO): void {
    if (!excessPayment.originIncomeReceipt?.paymentGroupId) {
      this.toastService.showToast('error', 'No se encontró información del grupo de pagos');
      return;
    }

    this.loadingFinancialSummary = true;
    this.showFinancialSummaryModal = true;
    this.selectedPaymentGroupSummary = null;

    this.excessPaymentsService.getPaymentGroupSummary(excessPayment.originIncomeReceipt.paymentGroupId).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.selectedPaymentGroupSummary = result.data;
          this.toastService.showToast('success', 'Resumen financiero cargado exitosamente');
        } else {
          this.toastService.showToast('error', result.errorMessage || 'Error al cargar el resumen financiero');
          this.closeFinancialSummaryModal();
        }
        this.loadingFinancialSummary = false;
      },
      error: (error) => {
        console.error('Error loading payment group summary:', error);
        this.toastService.showToast('error', 'Error al cargar el resumen financiero');
        this.loadingFinancialSummary = false;
        this.closeFinancialSummaryModal();
      }
    });
  }

  /**
   * Cerrar modal del resumen financiero
   */
  closeFinancialSummaryModal(): void {
    this.showFinancialSummaryModal = false;
    this.selectedPaymentGroupSummary = null;
    this.loadingFinancialSummary = false;
  }

  /**
   * Obtener clase CSS para el estado del plan de pago
   */
  getPaymentStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partially_paid':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtener datos de auditoría para un excedente de pago
   */
  getAuditData(excessPayment: ExcessPaymentResponseDTO): AuditInfo {
    const anyExcessPayment = excessPayment as any;
    return {
      createdBy: anyExcessPayment?.createdBy,
      createdAt: anyExcessPayment?.createdAt,
      updatedBy: anyExcessPayment?.updatedBy,
      updatedAt: anyExcessPayment?.updatedAt,
      deletedBy: anyExcessPayment?.deletedBy,
      deletedAt: anyExcessPayment?.deletedAt,
      isDeleted: anyExcessPayment?.isDeleted
    };
  }
}